import {useState, useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache, type Reference} from '@apollo/client';
import {BOOKMARK_FRAGMENT, UPDATE_BOOKMARK_MUTATION, type UpdateBookmarkOutput} from 'graphql/admin/bookmarks';
import {useSnackbar} from 'hooks';
import {EditIconButton, type EditIconButtonProps} from 'components/buttons';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import type {Bookmark} from 'types';
import {getErrorMessage} from 'utils';
import EditBookmarkDialog from './EditBookmarkDialog';

const updateBookmarksCache = (cache : ApolloCache<any>, {data} : {data : UpdateBookmarkOutput}) => {
  const updatedBookmark = data?.updateBookmark?.bookmark;
  if (updatedBookmark) cache.modify({
    fields: {
      getBookmarks({bookmarks: existingBookmarksRefs = [], ...getBookmarks}) {
        const updatedBookmarkCacheRef = cache.writeFragment({
          data: updatedBookmark,
          fragment: BOOKMARK_FRAGMENT
        });
        return {
          ...getBookmarks,
          bookmarks: existingBookmarksRefs.map((bookmarkRef : Reference) => bookmarkRef.__ref === updatedBookmarkCacheRef.__ref ? updatedBookmarkCacheRef : bookmarkRef)
        }
      }
    }
  });
};

type EditBookmarkIconButtonProps = EditIconButtonProps & {
  bookmark: Bookmark;
}

const EditBookmarkIconButton = ({bookmark} : EditBookmarkIconButtonProps) : JSX.Element => {
  const {key, name, uri} = bookmark;
  const bookmarkFolderKey = bookmark?.folders[0]?.key;
  
  const [open, setOpen] = useState(false);

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();
  const {successSnackbar} = useSnackbar();

  const onCompleted = (data : UpdateBookmarkOutput) => {
    const updatedBookmark = data?.updateBookmark?.bookmark;
    if (updatedBookmark) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.update.success" values={{ bookmarkName: updatedBookmark.name }} />);
      handleClose();
    }
  }

  const [updateBookmark, {loading, data, error, reset}] = useMutation<UpdateBookmarkOutput>(UPDATE_BOOKMARK_MUTATION, { update: updateBookmarksCache, onCompleted });

  const handleClick = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleEditBookmark = async ({newBookmarkName, newUrl, newFolderKey}) => {
    const folderMutationKeys : any = {};
    if (newFolderKey !== bookmarkFolderKey) {
      if (newFolderKey) folderMutationKeys.folderKeyToAdd = newFolderKey;
     
      if (bookmarkFolderKey) folderMutationKeys.folderKeyToRemove = bookmarkFolderKey;
    }
    await updateBookmark({
      variables: {
        updateBookmarkInput: {
          channel,
          bookmarkKey: key,
          newBookmarkName,
          newUrl,
          ...folderMutationKeys
        }
      }
    });
  }

  return <>
    <EditIconButton
      className='bg-admin-blue-400'
      labelId='admin.bookmarks.button.updateBookmark'
      onClick={handleClick}
    />
    <EditBookmarkDialog
      errorReason={data?.updateBookmark?.errorReason || getErrorMessage(error)}
      bookmarkName={name}
      bookmarkUrl={uri}
      bookmarkFolderKey={bookmarkFolderKey}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleEditBookmark={handleEditBookmark}
    />
  </>
}

export default EditBookmarkIconButton;